import { CSSResultGroup, LitElement, type TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { verticalTabsStyles } from './vertical-tabs-style';

/**
 * Vertical tabs are getting parentElement as a source of tabs - `sections` with `h2` elements or `link-in-navigation` with `h2` elements.
 *
 * Example:
 *
 * <div>
 *   <section>
 *     <h2>First</h2>
 *     My first content
 *   </section>
 *   <section>
 *     <h2>Second</h2>
 *     My second content
 *   </section>
 *   <ds-vertical-tabs></ds-vertical-tabs>
 * </div>
 * */

const isMobileOrTablet = (): boolean => matchMedia('(width < 1200px)').matches;

@customElement('ds-vertical-tabs')
export class VerticalTabs extends LitElement {
  /** @internal */
  static override styles: CSSResultGroup = verticalTabsStyles;

  /** Used to render vertical links content - tagElement is a source of links text, H2 is the default */
  @property() tagElement = 'H2';

  observer = new IntersectionObserver(
    entries => {
      let updated = false;
      const sections = Array.from(this.parentElement?.querySelectorAll('section[id], [link-in-navigation][id]') || []),
        links = Array.from<HTMLElement>(this.renderRoot.querySelectorAll('.ds-tab--vertical'));

      if (
        links.length === entries.filter(entry => entry.intersectionRatio === 1).length ||
        (!window.location.hash && entries.length === links.length)
      ) {
        return;
      }

      entries.forEach(entry => {
        if (updated) {
          return;
        }
        let section: Element | null | undefined;
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          section = entry.target;
          const index = sections.findIndex(b => {
            return (b as HTMLElement).id.toLowerCase() == (section as HTMLElement).getAttribute('id')?.toLowerCase();
          });

          if (links[index]) {
            this.#setActiveTab(links[index]);
            this.#alignVerticalTabIndicator(links[index]);
          }
        }

        updated = true;
      });
    },
    { root: null, rootMargin: '-96px 0px 0px 0px' }
  );

  override connectedCallback(): void {
    super.connectedCallback();

    if (isMobileOrTablet()) {
      return;
    }

    requestAnimationFrame(() => {
      /** Source to observe - `sections` with `id` elements and `link-in-navigation` with `id` elements */
      const sections = Array.from(this.parentElement?.querySelectorAll('section[id], [link-in-navigation][id]') || []),
        verticalTabs = this.renderRoot.querySelectorAll('.ds-tab--vertical');

      if (verticalTabs.length && !window.location.hash) {
        this.#setActiveTab(verticalTabs[0] as HTMLElement);
        window.scrollTo(0, 0);
      } else if (window.location.hash) {
        setTimeout(() => {
          const targetElement = document.querySelector(window.location.hash),
            links = Array.from<HTMLElement>(this.renderRoot.querySelectorAll('.ds-tab--vertical'));
          const index = links.findIndex(link => {
            return (link as HTMLAnchorElement).hash == window.location.hash;
          });
          if (targetElement) {
            links[index].click();
            targetElement.scrollIntoView();
          }
        }, 100);
      }

      if (sections.length) {
        sections.forEach(section => this.observer.observe(section));
      }
    });

    window.addEventListener(
      'hashchange',
      () => {
        const links = Array.from<HTMLElement>(this.renderRoot.querySelectorAll('.ds-tab--vertical'));
        const index = links.findIndex(link => {
          return (link as HTMLAnchorElement).hash == window.location.hash;
        });
        links[index].click();
      },
      false
    );
  }

  override disconnectedCallback(): void {
    this.observer?.disconnect();

    super.disconnectedCallback();
  }

  override render(): TemplateResult {
    /** generates nav links from parentElement content */
    const links = Array.from(this.parentElement?.querySelectorAll('.header-anchor, [link-in-navigation]') || [])
      .map(element => {
        if (element.parentElement?.tagName === this.tagElement) {
          if (element.parentElement.parentNode) {
            (element.parentElement.parentNode as Element).id = element.parentElement.id;
          }
          return element.parentElement as Element;
        } else if (element.hasAttribute('link-in-navigation')) {
          return element;
        }
        return null;
      })
      .filter(element => element !== null);

    return html`
      <div vertical class="ds-tabs">
        <div class="ds-tabs__container">
          <nav class="ds-tabs-wrapper" aria-label="Contents">
            ${links.map((variant: Element | null, index) => {
              const id = variant?.id ? `ds-vertical-tab-${variant.id}` : `ds-vertical-tab-${index}`;

              return html` <a
                href=${`#${variant?.id}`}
                class="ds-tab--vertical"
                id=${id}
                @click=${this.#onClick}
                >${(variant as HTMLElement)?.textContent || variant?.getAttribute('link-in-navigation-text')}</a
              >`;
            })}
          </nav>
          <div class="ds-tabs__vertical-slider">
            <div class="ds-tabs__vertical-indicator"></div>
          </div>
        </div>
      </div>
    `;
  }

  #onClick(event: Event) {
    this.observer?.disconnect();

    const link = event.target as HTMLElement;

    this.#setActiveTab(link);
    this.#alignVerticalTabIndicator(link);

    const indicator = this.renderRoot.querySelector('.ds-tabs__vertical-indicator');
    indicator?.addEventListener('transitionend', () => {
      setTimeout(() => {
        /** Source to observe - `sections` with `id` elements and `link-in-navigation` with `id` elements */
        const sections = Array.from(
          this.parentElement?.querySelectorAll('section[id], [link-in-navigation][id]') || []
        );
        if (sections.length) {
          sections.forEach(section => this.observer.observe(section));
        }
      }, 500);
    });
  }

  #setActiveTab(verticalTab: HTMLElement): void {
    const currentVerticalTabLink = this.renderRoot.querySelector('[aria-current="page"]'),
      verticalTabs = this.renderRoot.querySelectorAll('.ds-tab--vertical');

    currentVerticalTabLink?.removeAttribute('aria-current');
    verticalTab.setAttribute('aria-current', 'page');

    verticalTabs.forEach(v => v.classList.remove('active'));
    verticalTab.classList.add('active');
  }

  #alignVerticalTabIndicator(tab: Element): void {
    const currentVerticalIndicatorElement = this.renderRoot.querySelector(
        '.ds-tabs__vertical-indicator'
      ) as HTMLElement,
      tabsWrapper = this.renderRoot.querySelector('.ds-tabs-wrapper');

    if (!currentVerticalIndicatorElement || !tabsWrapper) {
      return;
    }
    currentVerticalIndicatorElement.style.top = `${
      tab.getBoundingClientRect().top - tabsWrapper.getBoundingClientRect().top
    }px`;
    currentVerticalIndicatorElement.style.height = `${tab.getBoundingClientRect().height}px`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ds-vertical-tabs': VerticalTabs;
  }
}
